const ENTITY_ALIASES = {
  customer: ["kunde", "kunder", "customer", "customers"],
  report: ["rapport", "rapporter", "report", "reports"],
  project: ["prosjekt", "prosjekter", "project", "projects"],
  case: ["sak", "saker", "case", "cases"],
  hse_document: ["hse_document", "hmsdokument", "hmsdokumenter"],
};

const LATEST_WORDS =
  "(?:siste|nyeste|seneste|latest|newest|last|recent|most\\s+recent)";
const CUSTOMER_WORDS = "(?:kunde|kunder|customer|customers)";
const FOR_WORDS = "(?:på|for|til|hos|on|for|at|from)";
const ENTITY_TOKENS =
  "(rapport|rapporter|report|reports|prosjekt|prosjekter|project|projects|sak|saker|case|cases|hse_document)(?:et|en|a|the)?";
const FIND_ENTITY_TOKENS =
  "kunde|kunder|customer|customers|rapport|rapporter|report|reports|prosjekt|prosjekter|project|projects|sak|saker|case|cases|hse_document|hms dokument|hms-dokument|hms dokumenter|hse document|hse documents|ks dokument|ks dokumenter";
const CREATED_WORDS = "(?:opprettet|opprettede|created)";

function cleanQuery(text) {
  return String(text || "")
    .trim()
    .replace(/\s+/g, " ");
}

/** Collapse multi-word HSE document phrases to a single intent token. */
function normalizeEntityPhrases(text) {
  return String(text || "")
    .replace(/\bhms\s*\/\s*ks[\s-]?(?:dokument(?:er)?|document(?:s)?)\b/gi, "hse_document")
    .replace(/\bhms[\s-]?(?:dokument(?:er)?)\b/gi, "hse_document")
    .replace(/\bhse[\s-]?(?:document(?:s)?)\b/gi, "hse_document")
    .replace(/\bks[\s-]?(?:dokument(?:er)?)\b/gi, "hse_document");
}

/** Strip conversational filler so intent regexes can match natural questions. */
export function normalizeInput(text) {
  return normalizeEntityPhrases(
    cleanQuery(text)
      .replace(/[?!.]+$/g, "")
      .replace(
        /^(?:hva\s+(?:er|var)|what\s+(?:is|was)|what'?s|vis\s+meg|show\s+me|fortell\s+meg|tell\s+me)\s+/i,
        ""
      )
      .replace(/\s+(?:i|in)\s+promang\s*$/i, "")
  ).trim();
}

function normalizeEntityToken(token) {
  const lower = String(token || "")
    .toLowerCase()
    .trim()
    .replace(/^(prosjekt|rapport|sak|project|report|case)(et|en|a|the)$/i, "$1");
  return normalizeEntityPhrases(lower).replace(/\s+/g, "_");
}

function entityFromToken(token) {
  if (!token) return null;
  const lower = normalizeEntityToken(token);
  return (
    Object.entries(ENTITY_ALIASES).find(([, aliases]) =>
      aliases.includes(lower)
    )?.[0] || null
  );
}

/**
 * Parse a natural-language workspace query into a structured intent.
 * No LLM — regex and keyword matching only.
 */
export function parseWorkspaceQuery(rawInput) {
  const input = cleanQuery(rawInput);
  if (!input) return { intent: "empty", query: "" };

  const normalized = normalizeInput(input);
  const lower = normalized.toLowerCase();

  if (/^(hjelp|help|\?|hva kan du|what can you)$/i.test(lower)) {
    return { intent: "help", query: input };
  }

  const latestForCustomer = normalized.match(
    new RegExp(
      `^${LATEST_WORDS}\\s+${ENTITY_TOKENS}\\s+(?:(?:${FOR_WORDS}\\s+)(?:${CUSTOMER_WORDS}\\s+)?|${CUSTOMER_WORDS}\\s+)(.+)$`,
      "i"
    )
  );
  if (latestForCustomer) {
    const entity = entityFromToken(latestForCustomer[1]);
    const customerName = cleanQuery(latestForCustomer[2]);
    if (entity && customerName) {
      return {
        intent: "latest_for_customer",
        entity,
        customerName,
        query: input,
      };
    }
  }

  const latestEntity = normalized.match(
    new RegExp(
      `^${LATEST_WORDS}(?:\\s+${CREATED_WORDS})?\\s+${ENTITY_TOKENS}(?:\\s+${CREATED_WORDS})?$`,
      "i"
    )
  );
  if (latestEntity) {
    const entity = entityFromToken(latestEntity[1]);
    if (entity) {
      return { intent: "latest_entity", entity, query: input };
    }
  }

  const statusWithEntity = normalized.match(
    new RegExp(
      `^(?:status|tilstand|state)\\s+${ENTITY_TOKENS}\\s+(.+)$`,
      "i"
    )
  );
  if (statusWithEntity) {
    const entity = entityFromToken(statusWithEntity[1]);
    const queryPart = cleanQuery(statusWithEntity[2]);
    if (entity && queryPart) {
      return { intent: "status_lookup", entity, query: queryPart, raw: input };
    }
  }

  const statusAliases = [
    { pattern: /^(?:rapportstatus|report\s+status)\s+(?:for\s+)?(.+)$/i, entity: "report" },
    { pattern: /^(?:prosjektstatus|project\s+status)\s+(?:for\s+)?(.+)$/i, entity: "project" },
    { pattern: /^(?:sakstatus|case\s+status)\s+(?:for\s+)?(.+)$/i, entity: "case" },
    {
      pattern: /^(?:hms[\s-]?dokumentstatus|hse[\s-]?document[\s-]?status|dokumentstatus|document\s+status)\s+(?:for\s+)?(.+)$/i,
      entity: "hse_document",
    },
  ];
  for (const { pattern, entity } of statusAliases) {
    const match = normalized.match(pattern);
    if (match?.[1]) {
      return {
        intent: "status_lookup",
        entity,
        query: cleanQuery(match[1]),
        raw: input,
      };
    }
  }

  const findMatch = normalized.match(
    new RegExp(
      `^(?:finn|søk|search|find|vis|show|hvor er|where is)\\s+(${FIND_ENTITY_TOKENS})\\s+(.+)$`,
      "i"
    )
  );
  if (findMatch) {
    const entity = entityFromToken(findMatch[1]);
    const query = cleanQuery(findMatch[2]);
    if (entity && query) {
      return { intent: "find_entity", entity, query, raw: input };
    }
  }

  const prefixedMatch = normalized.match(
    new RegExp(`^(${FIND_ENTITY_TOKENS})\\s+(.+)$`, "i")
  );
  if (prefixedMatch) {
    const entity = entityFromToken(prefixedMatch[1]);
    const query = cleanQuery(prefixedMatch[2]);
    if (entity && query) {
      return { intent: "find_entity", entity, query, raw: input };
    }
  }

  return { intent: "generic_search", query: input };
}

export function getEntityRoute(module, id) {
  const routes = {
    customer: `/dashboard/customers/${id}`,
    report: `/dashboard/service/reports/${id}`,
    project: `/dashboard/projects/${id}`,
    case: `/dashboard/cases/${id}`,
    contact: `/dashboard/contacts/${id}`,
    hse_document: `/dashboard/hse-ks/documents/${id}`,
  };
  return routes[module] || null;
}
